import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { hostedUiHost } from '../../config/cognitoRegistry';
import { getAppMarket } from '../../services/MarketService';

const SSOLogin: React.FC = () => {
    // Support both new (/auth/sso/:domain) and legacy (/login/sso/:orgSlug) routes
    const params = useParams<{ domain?: string; orgSlug?: string }>();
    const identifier = params.domain || params.orgSlug;

    useEffect(() => {
        if (!identifier) return;

        // Configuration for Cognito Hosted UI / SAML Redirect. This must be the
        // FULL Hosted-UI hostname — the registry's bare prefix (or the legacy
        // `auth.compliance-engine.io` default) does not resolve.
        const cognitoHost = hostedUiHost(process.env.REACT_APP_COGNITO_DOMAIN);
        const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;

        // This is the identifier for the SAML IdP in Cognito
        // Pattern: SAML-{identifier} (e.g., SAML-bristol.gov.uk or SAML-bristol-council)
        const identityProvider = `SAML-${identifier}`;

        const redirectUri = encodeURIComponent(`${window.location.origin}/auth/callback`);
        // Texas builds add aws.cognito.signin.user.admin so user-pool
        // self-service (attribute verification) works from this token; UK
        // PROD's client doesn't allow that scope (invalid_scope) — same gate
        // as getOAuthUrl in services/cognito.ts.
        const scope = encodeURIComponent(
            getAppMarket() === 'texas'
                ? 'openid email profile aws.cognito.signin.user.admin'
                : 'openid email profile'
        );

        // Construct the Authorization endpoint URL with identity_provider parameter
        // This forces Cognito to bypass its own login page and redirect straight to the client IdP
        const ssoUrl = `https://${cognitoHost}/oauth2/authorize?identity_provider=${identityProvider}&redirect_uri=${redirectUri}&response_type=code&client_id=${clientId}&scope=${scope}`;

        console.log(`[SSOLogin] Initiating SAML SSO redirect for identifier: ${identifier}, IdP: ${identityProvider}`);
        window.location.href = ssoUrl;
    }, [identifier]);

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
            <div className="bg-white p-8 rounded-2xl shadow-xl max-w-sm w-full text-center border border-slate-100">
                <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-6 animate-pulse">
                    <i className="fas fa-shield-alt"></i>
                </div>
                <h2 className="text-xl font-black text-slate-900 mb-2">Enterprise SSO</h2>
                <p className="text-slate-500 text-sm mb-6 font-medium">
                    Redirecting you to your organization's secure login portal...
                </p>
                <div className="flex justify-center">
                    <div className="hp-spinner-small"></div>
                </div>
            </div>
        </div>
    );
};

export default SSOLogin;
